import { z } from 'zod';
import { actionSchema } from '../data/schemas';
import type { BattleSessionState } from './battleSlice';

export const BATTLE_STORAGE_KEY = 'dnd-react-battle-v1';

const abilityScoresSchema = z.object({
  str: z.number(),
  dex: z.number(),
  con: z.number(),
  int: z.number(),
  wis: z.number(),
  cha: z.number(),
});

const modifiersSchema = z.object({
  damage: z.array(z.string()),
  conditions: z.array(z.string()),
});

const conditionKeySchema = z.enum([
  'prone',
  'restrained',
  'blinded',
  'poisoned',
  'charmed',
  'frightened',
  'deafened',
  'grappled',
  'incapacitated',
  'invisible',
  'paralyzed',
  'petrified',
  'stunned',
  'unconscious',
  'exhaustion',
]);

const conditionSchema = z.object({
  id: z.string().min(1),
  key: conditionKeySchema,
  rounds: z.number().int(),
  sourceUid: z.string().min(1).optional(),
  level: z.number().int().optional(),
  stepRounds: z.number().int().positive().optional(),
});

const combatantSchema = z.object({
  uid: z.string().min(1),
  baseId: z.string().min(1).optional(),
  kind: z.enum(['monster', 'pc']),
  name: z.string(),
  avatar: z.string().optional(),
  ac: z.number(),
  baseMaxHp: z.number(),
  hpMax: z.number(),
  hpCurrent: z.number(),
  tempHp: z.number(),
  abilities: abilityScoresSchema,
  resistances: modifiersSchema,
  vulnerabilities: modifiersSchema,
  immunities: modifiersSchema,
  actions: z.array(actionSchema.extend({ cooldown: z.number().min(0) })),
  statuses: z.array(conditionSchema),
  initiative: z.number().nullable(),
  initiativeRoll: z.number().optional(),
  initiativeModifier: z.number().optional(),
  justJoined: z.boolean().optional(),
});

const decisionRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    id: z.string().min(1),
    kind: z.literal('withinFiveFeet'),
    purpose: z.enum(['proneRoll', 'criticalHit']),
    targetUid: z.string().min(1),
    title: z.string(),
    message: z.string(),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('fearSourceVisible'),
    targetUid: z.string().min(1),
    title: z.string(),
    message: z.string(),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('saveOutcome'),
    targetUid: z.string().min(1),
    title: z.string(),
    message: z.string(),
    autoFail: z.boolean(),
    hasDisadvantage: z.boolean(),
  }),
]);

const damageResultSchema = z.object({
  rawAmount: z.number(),
  finalAmount: z.number(),
  type: z.string(),
  modifier: z.enum(['normal', 'resistant', 'vulnerable', 'immune']),
});

const targetResolutionSchema = z.object({
  targetUid: z.string().min(1),
  outcome: z.enum(['hit', 'miss', 'critical', 'critical-failure', 'save-fail', 'save-success']),
  attackRoll: z.number().optional(),
  attackTotal: z.number().optional(),
  damages: z.array(damageResultSchema),
});

const resolutionSchema = z.object({
  id: z.string().min(1),
  stage: z.enum([
    'idle',
    'awaitingDecision',
    'awaitingSaveOutcome',
    'presentingResults',
    'complete',
  ]),
  actorUid: z.string().min(1),
  action: actionSchema,
  targetUids: z.array(z.string().min(1)),
  rollMode: z.enum(['auto', 'normal', 'advantage', 'disadvantage']),
  currentTargetIndex: z.number().int().min(0),
  pendingDecision: decisionRequestSchema.optional(),
  decisionRequests: z.array(decisionRequestSchema),
  decisionAnswers: z.record(z.string(), z.boolean()),
  currentDecisionIndex: z.number().int().min(0),
  rolledDamages: z.array(z.object({ amount: z.number(), type: z.string() })),
  results: z.array(targetResolutionSchema),
});

const sessionSchema = z.object({
  battle: z.object({
    participants: z.array(combatantSchema),
    currentIndex: z.number().int(),
    round: z.number().int().min(1),
  }),
  selectedTargetUids: z.array(z.string()),
  selectedActionId: z.string().nullable(),
  resolution: z.unknown().nullable(),
  pendingExhaustionDeath: z
    .object({ targetUid: z.string().min(1), targetName: z.string() })
    .nullable()
    .default(null),
});

const envelopeSchema = z.object({ version: z.literal(1), session: z.unknown() });

function normalizeSession(value: z.infer<typeof sessionSchema>): BattleSessionState {
  const participants = value.battle.participants.map((participant) => ({
    ...participant,
    statuses: participant.statuses.map((status) =>
      status.key === 'exhaustion'
        ? {
            ...status,
            level: Math.min(6, Math.max(1, status.level ?? 1)),
            rounds: Math.max(1, status.rounds),
            stepRounds: Math.max(1, status.stepRounds ?? status.rounds),
          }
        : status,
    ),
  }));
  const participantUids = new Set(participants.map((participant) => participant.uid));
  const currentIndex = participants.length
    ? Math.min(Math.max(0, value.battle.currentIndex), participants.length - 1)
    : 0;
  const selectedTargetUids = value.selectedTargetUids.filter((uid) => participantUids.has(uid));
  const currentActor = participants[currentIndex];
  const selectedActionId =
    value.selectedActionId &&
    currentActor?.actions.some((action) => action.id === value.selectedActionId)
      ? value.selectedActionId
      : null;

  const parsedResolution = resolutionSchema.safeParse(value.resolution);
  let resolution = parsedResolution.success ? parsedResolution.data : null;
  if (
    resolution &&
    (!participantUids.has(resolution.actorUid) ||
      resolution.targetUids.some((uid) => !participantUids.has(uid)) ||
      resolution.results.some((result) => !participantUids.has(result.targetUid)) ||
      (resolution.pendingDecision && !participantUids.has(resolution.pendingDecision.targetUid)) ||
      ((resolution.stage === 'awaitingDecision' ||
        resolution.stage === 'awaitingSaveOutcome') &&
        !resolution.pendingDecision))
  ) {
    resolution = null;
  }

  const pendingExhaustionDeath =
    value.pendingExhaustionDeath && participantUids.has(value.pendingExhaustionDeath.targetUid)
      ? value.pendingExhaustionDeath
      : null;

  return {
    battle: { participants, currentIndex, round: value.battle.round },
    selectedTargetUids,
    selectedActionId,
    resolution,
    pendingExhaustionDeath,
  };
}

export function parseBattleSnapshot(input: string): BattleSessionState | undefined {
  try {
    const decoded: unknown = JSON.parse(input);
    const envelope = envelopeSchema.safeParse(decoded);
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'version' in decoded &&
      !envelope.success
    ) {
      return undefined;
    }
    const session = sessionSchema.safeParse(envelope.success ? envelope.data.session : decoded);
    return session.success ? normalizeSession(session.data) : undefined;
  } catch {
    return undefined;
  }
}

export function serializeBattleSnapshot(session: BattleSessionState): string {
  return JSON.stringify({ version: 1, session });
}

export function loadBattleSnapshot(storage: Pick<Storage, 'getItem'>): BattleSessionState | undefined {
  try {
    const raw = storage.getItem(BATTLE_STORAGE_KEY);
    return raw ? parseBattleSnapshot(raw) : undefined;
  } catch {
    return undefined;
  }
}

export function saveBattleSnapshot(
  storage: Pick<Storage, 'setItem'>,
  session: BattleSessionState,
): boolean {
  try {
    storage.setItem(BATTLE_STORAGE_KEY, serializeBattleSnapshot(session));
    return true;
  } catch {
    return false;
  }
}
